import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../api/client";
import type {
  Exercise,
  GraphicFamily,
  GraphicsCatalog,
  ImportedGraphic,
  LineGraphic,
  PlumeGraphic,
  TacticalGraphics,
  Zone,
  ZoneGraphic,
} from "../../api/types";
import { t } from "../../i18n/i18n";
import { type Draft, useUi } from "../../state/ui";
import { dossierWrite, graphicsOf, recordWrite, undoAction, useDossierHistory, writeFailure } from "../dossierWrites";
import { useDeployment } from "../units/useDeployment";
import { ZONES_KEY } from "../zones/useZones";
import { distanceM, hazardDistance, type LonLat } from "./geometry";
import { refuseIfDossierLocked, useDossierLocked } from "../../sharing/access";
import { plumeWind } from "../../weather";

/** Graphiques tactiques du dossier (zones, lignes typées, panaches NRBC du bac à sable de l'iPad) : lecture,
 *  création, modification, déplacement et suppression. Chaque modification enregistre l'état complet dans le
 *  dossier (sans recomposer les ordres, qui ne les citent pas) et s'inscrit dans l'historique de la carte (canal
 *  « graphiques ») ; « Annuler » défait par l'historique. */

export const NO_GRAPHICS: TacticalGraphics = { operationalGraphics: [], lineGraphics: [], cbrnPlumes: [] };

/** Graphique repéré avec sa famille. */
export type AnyGraphic =
  | { family: "operationalGraphics"; graphic: ZoneGraphic }
  | { family: "lineGraphics"; graphic: LineGraphic }
  | { family: "cbrnPlumes"; graphic: PlumeGraphic };

/** Bornes de saisie du serveur. */
export const RADIUS_RANGE: [number, number] = [50, 20_000];
export const WIND_RANGE: [number, number] = [0, 50];
export const MAX_LINE_POINTS = 200;

/** Secondes écoulées depuis le 1er janvier 2001 : horodatage des graphiques de l'iPad (`createdAt`). */
export function appleNow(): number {
  return Date.now() / 1000 - 978_307_200;
}

function newId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID().toUpperCase();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`.toUpperCase();
}

/** Graphiques tactiques du dossier (état complet) : défini avec les instantanés de l'historique. */
export { graphicsOf };

export function graphicsCount(graphics: TacticalGraphics): number {
  return graphics.operationalGraphics.length + graphics.lineGraphics.length + graphics.cbrnPlumes.length;
}

export function findGraphic(graphics: TacticalGraphics, id: string): AnyGraphic | null {
  const zone = graphics.operationalGraphics.find((g) => g.id === id);
  if (zone) return { family: "operationalGraphics", graphic: zone };
  const line = graphics.lineGraphics.find((g) => g.id === id);
  if (line) return { family: "lineGraphics", graphic: line };
  const plume = graphics.cbrnPlumes.find((g) => g.id === id);
  return plume ? { family: "cbrnPlumes", graphic: plume } : null;
}

/** Tous les graphiques, du plus ancien au plus récent. */
export function allGraphics(graphics: TacticalGraphics): AnyGraphic[] {
  const items: AnyGraphic[] = [
    ...graphics.operationalGraphics.map((graphic) => ({ family: "operationalGraphics" as const, graphic })),
    ...graphics.lineGraphics.map((graphic) => ({ family: "lineGraphics" as const, graphic })),
    ...graphics.cbrnPlumes.map((graphic) => ({ family: "cbrnPlumes" as const, graphic })),
  ];
  return items.sort((a, b) => a.graphic.createdAt - b.graphic.createdAt);
}

/** Catalogue des types (sigles, noms, couleurs, valeurs par défaut), servi par le serveur. */
export function useGraphicsCatalog() {
  return useQuery({ queryKey: ["graphics-catalog"], queryFn: api.graphicsCatalog, staleTime: Infinity });
}

/** Type d'un graphique dans le catalogue : sigle, nom et couleur. */
export function typeOf(
  catalog: GraphicsCatalog | undefined,
  item: AnyGraphic,
): { short: string; name: string; color: string } {
  if (item.family === "cbrnPlumes") {
    const agent = catalog?.agents.find((a) => a.kind === item.graphic.agent);
    return { short: `NRBC ${agent?.letter ?? "?"}`, name: agent ? `NRBC ${agent.name.toLowerCase()}` : "NRBC", color: agent?.color ?? "#F2CC0C" };
  }
  const entry =
    item.family === "operationalGraphics"
      ? catalog?.zones.find((z) => z.kind === item.graphic.kind)
      : catalog?.lines.find((l) => l.kind === item.graphic.kind);
  const color = item.graphic.colorHex ? `#${item.graphic.colorHex}` : (entry?.color ?? "#00A8DC");
  return { short: entry?.short ?? item.graphic.kind, name: entry?.name ?? item.graphic.kind, color };
}

/** Libellé affiché : celui du graphique, sinon son sigle (comme `effectiveLabel` de l'iPad). */
export function labelOf(catalog: GraphicsCatalog | undefined, item: AnyGraphic): string {
  return item.graphic.label || typeOf(catalog, item).short;
}

/** Point d'ancrage d'un graphique (centre, milieu de la ligne, point de relâchement), pour la fiche et le cadrage. */
export function anchorOf(item: AnyGraphic): LonLat {
  if (item.family === "operationalGraphics") return [item.graphic.longitude, item.graphic.latitude];
  if (item.family === "cbrnPlumes") return [item.graphic.releaseLon, item.graphic.releaseLat];
  const coords = item.graphic.coordinates;
  const mid = coords[Math.floor(coords.length / 2)]!;
  if (coords.length > 2) return [mid.longitude, mid.latitude];
  const [a, b] = [coords[0]!, coords[1]!];
  return [(a.longitude + b.longitude) / 2, (a.latitude + b.latitude) / 2];
}

/** Vue qui montre tout le graphique (sur environ 500 px) : centre et zoom, pour « Centrer » et la liste. */
export function viewOf(catalog: GraphicsCatalog | undefined, item: AnyGraphic): { at: LonLat; zoom: number } {
  let extent: number;
  if (item.family === "operationalGraphics") {
    extent = 2 * item.graphic.radiusMetres;
  } else if (item.family === "lineGraphics") {
    const lons = item.graphic.coordinates.map((c) => c.longitude);
    const lats = item.graphic.coordinates.map((c) => c.latitude);
    extent = distanceM([Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]);
  } else {
    const agent = catalog?.agents.find((a) => a.kind === item.graphic.agent);
    const reach = agent ? hazardDistance(agent.downwind_m, item.graphic.windSpeedKnots) : 10_000;
    extent = 2 * Math.max(reach, agent?.released_m ?? 1000);
  }
  const at = anchorOf(item);
  const zoom = Math.log2((156_543.03 * Math.cos((at[1] * Math.PI) / 180) * 500) / Math.max(extent * 1.2, 200));
  return { at, zoom: Math.min(15, Math.max(9, zoom)) };
}

/** Libellé du prochain graphique d'un sigle : le plus grand numéro employé plus un (« OBJ 3 » après « OBJ 1 » et
 *  « OBJ 2 », sans doublon après une suppression, là où l'iPad comptait les graphiques restants). */
function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function nextLabel(graphics: TacticalGraphics, family: GraphicFamily, short: string): string {
  const pattern = new RegExp(`^${escapeRegExp(short)} (\\d+)$`);
  const used = (graphics[family] as { label: string | null }[])
    .map((g) => pattern.exec(g.label ?? ""))
    .filter((m): m is RegExpExecArray => m !== null)
    .map((m) => Number(m[1]));
  return `${short} ${Math.max(0, ...used) + 1}`;
}

/** Libellé après un changement de type : un libellé automatique (« OBJ 2 ») suit le nouveau sigle (« ZE 1 ») ;
 *  un nom saisi est gardé. */
export function relabel(
  label: string | null,
  all: TacticalGraphics,
  family: GraphicFamily,
  from: string,
  to: string,
): string | null {
  const automatic = !label || new RegExp(`^${escapeRegExp(from)} \\d+$`).test(label);
  return automatic ? nextLabel(all, family, to) : label;
}

/** Graphiques avec `id` remplacé par `edit(graphique)`. */
/** Modification d'un graphique, calculée sur l'état courant (`all` : pour numéroter un libellé). */
export type GraphicEdit = (item: AnyGraphic, all: TacticalGraphics) => AnyGraphic["graphic"];

function replaced(graphics: TacticalGraphics, id: string, edit: GraphicEdit): TacticalGraphics {
  const item = findGraphic(graphics, id);
  if (!item) return graphics;
  const next = edit(item, graphics);
  return { ...graphics, [item.family]: (graphics[item.family] as { id: string }[]).map((g) => (g.id === id ? next : g)) };
}

interface Change {
  apply: (graphics: TacticalGraphics) => TacticalGraphics;
  /** Avis après enregistrement (lu après `apply`). */
  text: () => string;
  /** Action inscrite dans l'historique, en minuscule (« ajout de OBJ 1 »), pour « Annulé : … ». */
  label: () => string;
  /** Graphique à sélectionner une fois enregistré (création). */
  select?: { id: string; at: LonLat };
}

export function useGraphics() {
  const queryClient = useQueryClient();
  const params = useUi((s) => s.deploymentParams);
  const exerciseId = useUi((s) => s.exerciseId);
  const notify = useUi((s) => s.notify);
  const requestSave = useUi((s) => s.requestSave);
  const select = useUi((s) => s.select);
  const { data: deployment } = useDeployment();
  const { data: catalog } = useGraphicsCatalog();
  const graphics = graphicsOf(deployment);
  // Les graphiques vivent dans le dossier enregistré (comme les déplacements et les ponts détruits).
  // Dossier en lecture seule pour le compte (docs/17 §3.2) : rien ne se trace, l'appui désigne le bandeau.
  const locked = useDossierLocked();
  const canEdit = Boolean(params?.exercise_id) && !locked;
  useDossierHistory();

  const mutation = useMutation({
    // Une modification à la fois, chacune sur l'état laissé par la précédente : portée du dossier, commune à
    // toutes ses écritures (déplacements, ponts, fuseau… : pas de 412 entre elles).
    mutationFn: (change: Change) =>
      dossierWrite(queryClient, (target, opo, current) => api.setGraphics(target, change.apply(graphicsOf(current)), opo), {
        notSaved: t("Enregistrez le dossier pour dessiner des graphiques."),
        channel: "graphiques",
        orders: false,
      }),
    onSuccess: (write, change) => {
      const selection = useUi.getState().selection;
      if (change.select) {
        select({ layer: "graphic", id: change.select.id, lngLat: change.select.at });
      } else if (selection?.layer === "graphic" && !findGraphic(graphicsOf(write.result), selection.id)) {
        select(null);
      }
      const recorded = recordWrite(write, change.label());
      notify({
        text: change.text(),
        detail: t("Graphiques tactiques enregistrés dans le dossier."),
        tone: "ok",
        action: undoAction(recorded),
      });
    },
    onError: (error) => writeFailure(queryClient, error, t("Graphique non enregistré")),
  });

  /** Sans dossier enregistré, le bouton « Enregistrer » (ou « Créer le dossier ») des Données initiales clignote
   *  pour désigner l'action attendue. `action` arrive traduite (`t("dessiner des graphiques")`). */
  const guard = (action: string): boolean => {
    if (refuseIfDossierLocked()) return false;
    // État relu à l'appel (et non au rendu) : le dossier vient peut-être d'être enregistré.
    if (useUi.getState().deploymentParams?.exercise_id) return true;
    requestSave();
    notify({
      text: exerciseId
        ? t("Enregistrez les modifications du dossier pour {action}", { action })
        : t("Créez le dossier pour {action}", { action }),
      detail: t("Les graphiques tactiques sont mémorisés dans le dossier d'exercice."),
      tone: "info",
    });
    return false;
  };

  /** Graphique tracé sur la carte : zone ou panache au point cliqué, ligne par ses points. */
  const create = (draft: Draft & { family: GraphicFamily }, points: LonLat[]) => {
    const [first] = points;
    if (!first || !guard(t("dessiner des graphiques"))) return;
    const id = newId();
    const createdAt = appleNow();
    const slug = params?.map_slug ?? null;
    const shown = queryClient.getQueryData<Exercise[]>(["exercises"])?.find((e) => e.id === exerciseId);
    const wind = plumeWind(queryClient, slug, shown, queryClient.getQueryData<Zone[]>(ZONES_KEY)?.find((z) => z.slug === slug) ?? null);
    let label = "";
    const short =
      draft.family === "cbrnPlumes"
        ? `NRBC ${catalog?.agents.find((a) => a.kind === draft.kind)?.letter ?? "?"}`
        : ((draft.family === "operationalGraphics" ? catalog?.zones : catalog?.lines)?.find((t) => t.kind === draft.kind)
            ?.short ?? draft.kind);
    const apply = (current: TacticalGraphics): TacticalGraphics => {
      label = nextLabel(current, draft.family, short);
      if (draft.family === "operationalGraphics") {
        const zone: ZoneGraphic = {
          id,
          kind: draft.kind as ZoneGraphic["kind"],
          latitude: first[1],
          longitude: first[0],
          radiusMetres: draft.radius_m ?? 1000,
          label,
          colorHex: null,
          createdAt,
        };
        return { ...current, operationalGraphics: [...current.operationalGraphics, zone] };
      }
      if (draft.family === "lineGraphics") {
        const line: LineGraphic = {
          id,
          kind: draft.kind as LineGraphic["kind"],
          coordinates: points.map(([longitude, latitude]) => ({ latitude, longitude })),
          label,
          colorHex: null,
          createdAt,
        };
        return { ...current, lineGraphics: [...current.lineGraphics, line] };
      }
      // Vent de la météo enregistrée du dossier (ses options Météo), sinon celui de l'iPad : dérive vers l'est, vent de
      // 10 nœuds ; à régler dans la fiche.
      const plume: PlumeGraphic = {
        id,
        releaseLat: first[1],
        releaseLon: first[0],
        agent: draft.kind as PlumeGraphic["agent"],
        plumeBearingDegrees: wind.bearing,
        windSpeedKnots: wind.knots,
        label,
        createdAt,
      };
      return { ...current, cbrnPlumes: [...current.cbrnPlumes, plume] };
    };
    const at = draft.family === "lineGraphics" ? (points[Math.floor(points.length / 2)] ?? first) : first;
    mutation.mutate({
      apply,
      text: () => t("Graphique ajouté : {label}", { label }),
      label: () => t("ajout de {label}", { label }),
      select: { id, at },
    });
  };

  /** Modification d'un graphique (nom, type, rayon, agent, météo) ; `what` arrive traduit (`t("Rayon modifié")`). */
  const edit = (id: string, change: GraphicEdit, what: string) => {
    if (!guard(t("modifier les graphiques"))) return;
    const item = findGraphic(graphics, id);
    const name = item ? labelOf(catalog, item) : t("graphique");
    mutation.mutate({
      apply: (current) => replaced(current, id, change),
      text: () => t("{what} : {name}", { what, name }),
      label: () => t("modification de {name}", { name }),
    });
  };

  /** Glissement sur la carte : tout le graphique décalé de (dLon, dLat). */
  const move = (id: string, dLon: number, dLat: number, onSettled?: () => void) => {
    if (!guard(t("déplacer les graphiques"))) {
      onSettled?.();
      return;
    }
    const item = findGraphic(graphics, id);
    const name = item ? labelOf(catalog, item) : t("graphique");
    mutation.mutate(
      {
        apply: (current) =>
          replaced(current, id, (found) => {
            if (found.family === "operationalGraphics") {
              const ring = found.graphic.ring?.map((c) => ({ latitude: c.latitude + dLat, longitude: c.longitude + dLon }));
              return {
                ...found.graphic,
                latitude: found.graphic.latitude + dLat,
                longitude: found.graphic.longitude + dLon,
                ...(ring ? { ring } : {}),
              };
            }
            if (found.family === "cbrnPlumes") {
              return { ...found.graphic, releaseLat: found.graphic.releaseLat + dLat, releaseLon: found.graphic.releaseLon + dLon };
            }
            const coordinates = found.graphic.coordinates.map((c) => ({
              latitude: c.latitude + dLat,
              longitude: c.longitude + dLon,
            }));
            return { ...found.graphic, coordinates };
          }),
        text: () => t("Graphique déplacé : {name}", { name }),
        label: () => t("déplacement de {name}", { name }),
      },
      { onSettled },
    );
  };

  const remove = (id: string) => {
    if (!guard(t("supprimer les graphiques"))) return;
    const item = findGraphic(graphics, id);
    if (!item) return;
    const name = labelOf(catalog, item);
    mutation.mutate({
      apply: (current) => ({
        ...current,
        [item.family]: (current[item.family] as { id: string }[]).filter((g) => g.id !== id),
      }),
      text: () => t("Graphique supprimé : {name}", { name }),
      label: () => t("suppression de {name}", { name }),
    });
  };

  /** Graphiques importés d'un fichier de situation : ajoutés d'un bloc, annulables d'un bloc. */
  const importMany = (items: ImportedGraphic[], source: string, onDone?: () => void) => {
    if (!items.length || !guard(t("importer des graphiques"))) return;
    const count = items.length;
    mutation.mutate(
      {
        apply: (current) => {
          const next: TacticalGraphics = {
            operationalGraphics: [...current.operationalGraphics],
            lineGraphics: [...current.lineGraphics],
            cbrnPlumes: [...current.cbrnPlumes],
          };
          const createdAt = appleNow();
          for (const item of items) {
            // Nouvel identifiant : un même fichier importé deux fois ne heurte pas le premier import.
            (next[item.family] as { id: string; createdAt: number }[]).push({ ...item.graphic, id: newId(), createdAt });
          }
          return next;
        },
        text: () =>
          count > 1
            ? t("{n} graphiques importés ({source})", { n: count, source })
            : t("Graphique importé ({source})", { source }),
        label: () => (count > 1 ? t("import de {n} graphiques", { n: count }) : t("import d'un graphique")),
      },
      { onSuccess: () => onDone?.() },
    );
  };

  return { graphics, catalog, canEdit, busy: mutation.isPending, guard, create, edit, move, remove, importMany };
}
